import { LinkIcon } from "@heroicons/react/16/solid";
import { PencilIcon, TrashIcon, UserIcon } from "@heroicons/react/24/solid";
import { Character } from "@moe/priv/model/character";
import { useNavigate } from "@tanstack/react-router";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { ThinCharacterCard } from "@web/components/ThinCharacterCard";
import { useCreateChat } from "@web/hooks/use-create-chat";
import { useDeleteChar } from "@web/hooks/use-delete-char";
import { toast } from "sonner";

interface Props {
  char: Character;
  showEdit?: boolean;
  showDelete?: boolean;
}

export function ProfileCard({ char, showEdit = false, showDelete = false }: Props) {
  const [_, copyToClipboard] = useCopyToClipboard();
  const navigate = useNavigate();
  const createChat = useCreateChat();
  const { deleteChar } = useDeleteChar();

  const cardEditHandler = () => {
    navigate({
      to: "/studio/update",
      search: { cardID: char.id }
    });
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/character/${char.id}`;
    copyToClipboard(shareUrl);
    toast.success("Share link copied to clipboard");
  };

  const handleGoToPage = () => {
    navigate({ to: `/character/${char.id}` });
  };

  const handleDelete = () => {
    deleteChar(char.id);
  };

  const actions = menuActionsConfig({
    onShare: handleShare,
    onGoToPage: handleGoToPage,
    onEdit: showEdit ? cardEditHandler : undefined,
    onDelete: showDelete ? handleDelete : undefined
  });

  return <ThinCharacterCard char={char} actions={actions} onClick={() => createChat(char)} />;
}

interface Action {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onSelect: (e: Event) => void;
  className?: string;
}

const menuActionsConfig = (handlers: {
  onShare: (e: Event) => void;
  onGoToPage: (e: Event) => void;
  onEdit?: (e: Event) => void;
  onDelete?: (e: Event) => void;
}): Action[] => {
  const actions: Action[] = [
    {
      label: "Go to Page",
      icon: UserIcon,
      onSelect: handlers.onGoToPage
    },
    {
      label: "Share",
      icon: LinkIcon,
      onSelect: handlers.onShare
    }
  ];

  if (handlers.onEdit) {
    actions.push({
      label: "Edit",
      icon: PencilIcon,
      onSelect: handlers.onEdit
    });
  }

  if (handlers.onDelete) {
    actions.push({
      label: "Delete",
      icon: TrashIcon,
      onSelect: handlers.onDelete,
      className: "text-danger"
    });
  }

  return actions;
};
